import * as React from 'react'
import { useAppDispatch, useAppSelector } from '@store/index'
import { ReceptionToCheckOutList } from '@modules/reception/to-checkout/reception-to-check-out-list'
import { ReceptionToCheckOutFilters } from '@modules/reception/to-checkout/reception-to-check-out-filters'
import { LoaderPlaceholder } from '@components/loader-placeholder'
import { formatDate } from '@helpers/date-helper'
import { getReceptionBookings } from '@store/actions/reception-actions'
import { ReceptionCheckType } from '@models/reception'
import { ReceptionWebSocketHandler } from '@modules/reception/reception-web-socket-handler'
import * as queryString from 'querystring'
import { useApiRequest } from '@components/hooks/use-api-request'
import { hasVip } from '@helpers/improvements'
import { CustomReactSelectOption } from '@components/custom-react-select'
import { createSelectOption } from '@helpers/utils'
import { selectReceptionBookings, setReceptionBookings } from '@store/slices/reception-slice'

export interface ReceptionToCheckOutFiltersParams {
  date_to: Date
  sort_by: CustomReactSelectOption<string, 'name' | 'departure_time' | 'apartment.name'>
  sort_direction: CustomReactSelectOption<string, 'asc' | 'desc'>
  search: string
  all_checkouts: boolean
  only_vip: boolean
}

interface ReceptionToCheckoutProps {
  resortId: number
  receptionDateFilter: Date
}

export const ReceptionToCheckout: React.FC<ReceptionToCheckoutProps> = ({ resortId, receptionDateFilter }) => {
  const { q, all_checkouts } = queryString.parse(location.search.replace('?', ''))

  const defaultFilters: ReceptionToCheckOutFiltersParams = {
    date_to: receptionDateFilter,
    sort_by: createSelectOption('Numer lokalu', 'apartment.name'),
    sort_direction: createSelectOption('Rosnąco', 'asc'),
    search: String(q || ''),
    all_checkouts: Boolean(all_checkouts),
    only_vip: false,
  }
  const [filters, setFilters] = React.useState<ReceptionToCheckOutFiltersParams>(defaultFilters)
  const [totalVip, setTotalVip] = React.useState(0)

  const dispatch = useAppDispatch()

  const bookings = useAppSelector(selectReceptionBookings)

  const { isLoading, action: fetch } = useApiRequest(async () => {
    await dispatch(setReceptionBookings([]))
    await dispatch(
      getReceptionBookings(
        resortId,
        formatDate(filters.date_to),
        ReceptionCheckType.TO_CHECKOUT,
        filters.all_checkouts,
      ),
    )
  })

  React.useEffect(() => {
    fetch()
    return () => {
      dispatch(setReceptionBookings([]))
    }
  }, [formatDate(filters.date_to), resortId, filters.all_checkouts])

  React.useEffect(() => {
    if (!isLoading) {
      setTotalVip(bookings.filter(hasVip).length)
    }
  }, [bookings, isLoading])

  return (
    <>
      <ReceptionWebSocketHandler />

      <ReceptionToCheckOutFilters
        defaultFilters={defaultFilters}
        filters={filters}
        setFilters={setFilters}
        totalVip={totalVip}
      />

      <LoaderPlaceholder content={!isLoading}>
        <ReceptionToCheckOutList resortId={resortId} filters={filters} bookings={bookings} />
      </LoaderPlaceholder>
    </>
  )
}
